import { z } from 'zod';
import { esquemaMotivo } from './comunes.js';
/**
 * Audiometria tonal estructurada (F6.2, SPEC-02, plan maestro §7.5).
 *
 * Cada punto lleva TODA su semantica: oido, via (aerea u osea), frecuencia,
 * umbral en dB HL, si hubo enmascaramiento y si no hubo respuesta. Sin eso no
 * hay audiograma, solo numeros sueltos, y el plan pide no reconstruir uno asi.
 * El original (PDF o imagen del equipo) se adjunta al estudio y es lo que vale.
 * No se calculan promedios ni se clasifica la perdida: eso es interpretacion.
 */
/** Frecuencias convencionales de la audiometria tonal, en Hz. */
export const FRECUENCIAS_AUDIOMETRICAS_HZ = [
    125, 250, 500, 750, 1000, 1500, 2000, 3000, 4000, 6000, 8000,
];
export const VIAS_AUDIOMETRICAS = ['aerea', 'osea'];
export const NOMBRE_DE_VIA = {
    aerea: 'Vía aérea',
    osea: 'Vía ósea',
};
export const OIDOS = ['derecha', 'izquierda'];
export const NOMBRE_DE_OIDO = {
    derecha: 'Oído derecho',
    izquierda: 'Oído izquierdo',
};
export const ORIGENES_DE_AUDIOMETRIA = ['transcrito', 'importado'];
/**
 * Etiquetas de oido que se aceptan al importar. OD y OI en espanol; AD y AS
 * son las latinas (auris dextra y sinistra). D, I, R o L se rechazan: adivinar
 * el lado es el error que se evita (AC-21).
 */
const LADO_DE_ETIQUETA_DE_OIDO = {
    DERECHA: 'derecha',
    IZQUIERDA: 'izquierda',
    OD: 'derecha',
    OI: 'izquierda',
    AD: 'derecha',
    AS: 'izquierda',
};
export function oidoDeEtiqueta(etiqueta) {
    const clave = etiqueta.trim().toUpperCase();
    return Object.hasOwn(LADO_DE_ETIQUETA_DE_OIDO, clave)
        ? (LADO_DE_ETIQUETA_DE_OIDO[clave] ?? null)
        : null;
}
const esquemaPunto = z
    .object({
    /** `derecha`/`izquierda`, o la etiqueta del informe (OD, OI, AD, AS). */
    oido: z.string().trim().min(1).max(10),
    via: z.enum(VIAS_AUDIOMETRICAS),
    frecuenciaHz: z
        .number()
        .int()
        .refine((f) => FRECUENCIAS_AUDIOMETRICAS_HZ.includes(f), {
        message: 'Frecuencia no convencional',
    }),
    /** Limites de plausibilidad, no clinicos. Sin respuesta: el maximo presentado. */
    umbralDbHl: z.number().int().min(-20).max(130),
    sinRespuesta: z.boolean().default(false),
    enmascarado: z.boolean().default(false),
})
    .strict();
export const esquemaGuardarAudiometria = z
    .object({
    versionEsperada: z.number().int().min(0),
    origen: z.enum(ORIGENES_DE_AUDIOMETRIA),
    equipo: z.string().trim().max(120).default(''),
    /** Contexto: cabina, transductor, colaboracion, lo que el informe diga. */
    condiciones: z.string().trim().max(500).default(''),
    puntos: z.array(esquemaPunto).min(1).max(100),
    /** Obligatorio si el resultado ya era definitivo: el cambio lo corrige. */
    motivo: esquemaMotivo.optional(),
})
    .strict()
    .superRefine((datos, ctx) => {
    const vistos = new Set();
    datos.puntos.forEach((punto, indice) => {
        const oido = oidoDeEtiqueta(punto.oido);
        if (!oido) {
            ctx.addIssue({
                code: 'custom',
                message: `Oído desconocido: «${punto.oido}». Use derecha, izquierda, OD, OI, AD o AS`,
                path: ['puntos', indice, 'oido'],
            });
            return;
        }
        const clave = `${oido}:${punto.via}:${punto.frecuenciaHz}`;
        if (vistos.has(clave)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Hay dos umbrales para el mismo oído, vía y frecuencia',
                path: ['puntos', indice],
            });
        }
        vistos.add(clave);
    });
});
const orden = (p) => `${OIDOS.indexOf(p.oido)}:${VIAS_AUDIOMETRICAS.indexOf(p.via)}:${String(p.frecuenciaHz).padStart(5, '0')}`;
/**
 * Los puntos de una captura con su oido canonico y en orden estable. El oido
 * sale de la etiqueta de CADA punto, nunca de su posicion en la lista.
 */
export function puntosNormalizados(puntos) {
    return puntos
        .map((p) => {
        const oido = oidoDeEtiqueta(p.oido);
        if (!oido)
            throw new Error(`Oído desconocido: ${p.oido}`);
        return {
            oido,
            via: p.via,
            frecuenciaHz: p.frecuenciaHz,
            umbralDbHl: p.umbralDbHl,
            sinRespuesta: p.sinRespuesta,
            enmascarado: p.enmascarado,
        };
    })
        .sort((a, b) => orden(a).localeCompare(orden(b)));
}
//# sourceMappingURL=audiometria.js.map